/**
 * Determinismus-Hash des Umsatzsteuer-Rechenkerns (R12): 64-Bit-FNV-1a über kanonisches JSON.
 *
 * Der Hash dient der Änderungs-Erkennung zwischen Freigabe-Snapshot und Live-Daten
 * (Spec Sektion 8). Er hängt nur von fachlichen Eingaben ab – nicht von `now`, nicht von
 * Funktionen, nicht von Metadaten wie updatedAt/updatedBy.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {VatEngineInput} from './types';

const FNV_OFFSET_BASIS = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const MASK_64 = 0xffffffffffffffffn;

export const fnv1a64 = (text: string): string => {
    const bytes = new TextEncoder().encode(text);
    let hash = FNV_OFFSET_BASIS;
    for (const byte of bytes) {
        hash ^= BigInt(byte);
        hash = (hash * FNV_PRIME) & MASK_64;
    }
    return hash.toString(16).padStart(16, '0');
};

/** Meta-Felder ohne Einfluss auf Zahlen oder Zuordnung. */
export const HASH_IGNORED_KEYS: ReadonlySet<string> = new Set([
    'updatedAt', 'updatedBy', 'setupCompletedAt', 'dismissedHints', 'note', 'uid', 'displayName',
    // Reine Anzeigefelder der Lite-Typen (Stufe 2): Einheiten-Label, Mietername, Konto-Bezeichnung ändern keine Zahl
    'label', 'tenantsName', 'accountLabel',
]);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date);

/**
 * Kanonisches JSON: Schlüssel alphabetisch, Arrays aus Objekten nach ihrer kanonischen Form
 * sortiert (Permutations-Invarianz, T17), Primitive-Arrays in Reihenfolge, Dates als ISO-String,
 * Funktionen und undefined ausgelassen, NaN/Infinity als null.
 */
export const canonicalize = (value: unknown): string => {
    if (value === null || value === undefined) {
        return 'null';
    }
    if (value instanceof Date) {
        return JSON.stringify(Number.isNaN(value.getTime()) ? null : value.toISOString());
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? JSON.stringify(value) : 'null';
    }
    if (typeof value === 'string' || typeof value === 'boolean') {
        return JSON.stringify(value);
    }
    if (typeof value === 'bigint') {
        return JSON.stringify(value.toString());
    }
    if (Array.isArray(value)) {
        const items = value.filter(item => typeof item !== 'function' && item !== undefined).map(canonicalize);
        if (value.length > 0 && value.every(isPlainObject)) {
            items.sort();
        }
        return `[${items.join(',')}]`;
    }
    if (isPlainObject(value)) {
        const parts: string[] = [];
        for (const key of Object.keys(value).sort()) {
            const entry = value[key];
            if (HASH_IGNORED_KEYS.has(key) || entry === undefined || typeof entry === 'function') {
                continue;
            }
            parts.push(`${JSON.stringify(key)}:${canonicalize(entry)}`);
        }
        return `{${parts.join(',')}}`;
    }
    return 'null';
};

export const computeInputHash = (input: VatEngineInput): string =>
    fnv1a64(canonicalize({
        landlordID: input.landlordID,
        range: input.range,
        profile: input.profile,
        ownership: input.ownership,
        pendingChanges: input.pendingChanges,
        periods: input.periods,
        units: input.units,
        propertyProfiles: input.propertyProfiles,
        bookings: input.bookings,
        payments: input.payments,
        priorPayments: input.priorPayments,
        expenses: input.expenses,
        adjustments: input.adjustments,
        monitor19Input: input.monitor19Input,
    }));
